
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { getFolderShares, removeShare } from "./services/folderSharesService";
import { FolderShareWithUser } from "./types";

export const useManageFolderShares = (folderId: string) => {
  const [shares, setShares] = useState<FolderShareWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);

  // Fetch folder shares
  const fetchShares = useCallback(async () => {
    if (!folderId) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const folderShares = await getFolderShares(folderId);
      setShares(folderShares);
    } catch (error: any) {
      console.error("Error fetching folder shares:", error);
      setError(error.message || "Kunde inte hämta delningar");
      toast.error(error.message || "Kunde inte hämta delningar");
    } finally {
      setIsLoading(false);
    }
  }, [folderId]);

  // Handle removing a share
  const handleRemoveShare = useCallback(async (shareId: string) => {
    try {
      setIsRemoving(shareId);
      
      const success = await removeShare(shareId);
      
      if (success) {
        setShares(prevShares => prevShares.filter(share => share.id !== shareId));
        toast.success("Delningen har tagits bort");
      }
    } catch (error: any) {
      console.error("Error removing share:", error);
      toast.error(error.message || "Kunde inte ta bort delningen");
    } finally {
      setIsRemoving(null);
    }
  }, []);

  // Fetch shares when component mounts or folderId changes
  useEffect(() => {
    if (folderId) {
      fetchShares();
    }
  }, [folderId, fetchShares]);

  return {
    shares,
    isLoading,
    error,
    isRemoving,
    handleRemoveShare,
    refreshShares: fetchShares
  };
};
